/**
 * WAX Account Creation Plugin
 *
 * Wharfkit login plugin for handling WAX account creation flows.
 */

import { LoginPlugin, LoginContext, LoginHookTypes } from "@wharfkit/session";
import { clientLogger } from "../client-logger";
import type { WaxAccountCreationOptions } from "./types";

export class WaxAccountCreationPlugin implements LoginPlugin {
  public id = "wax-account-creation";
  private options: WaxAccountCreationOptions;

  constructor(options: WaxAccountCreationOptions = {}) {
    this.options = options;
  }

  register(context: LoginContext): void {
    // Add beforeLogin hook
    context.addHook(LoginHookTypes.beforeLogin, async (ctx: LoginContext) => {
      clientLogger.debug("Account Creation Plugin: beforeLogin");

      // Check if cloud wallet is selected via walletPluginIndex
      const selectedPlugin = ctx.walletPluginIndex !== undefined
        ? ctx.walletPlugins[ctx.walletPluginIndex]
        : null;

      if (selectedPlugin && this.options.email) {
        clientLogger.debug("Preparing Cloud Wallet with user data", {
          email: this.options.email,
          firstName: this.options.firstName,
          lastName: this.options.lastName,
        });

        (ctx as unknown as { userData: WaxAccountCreationOptions }).userData = {
          email: this.options.email,
          firstName: this.options.firstName,
          lastName: this.options.lastName,
        };
      }
    });

    // Add afterLogin hook
    context.addHook(LoginHookTypes.afterLogin, async (ctx: LoginContext) => {
      clientLogger.debug("Account Creation Plugin: afterLogin");

      // Note: The session is not available on LoginContext -
      // account creation callback should be handled via SessionKit login response
      if (ctx.permissionLevel) {
        const accountName = ctx.permissionLevel.actor.toString();
        const isTemp = accountName.endsWith(".wam");

        clientLogger.info("Account created/connected", { accountName, isTemp });

        this.options.onAccountCreated?.(accountName, isTemp);

        if (isTemp) {
          clientLogger.debug("Temp account created, will be activated during review submission");
        }
      }
    });
  }

  updateOptions(newOptions: Partial<WaxAccountCreationOptions>): void {
    this.options = { ...this.options, ...newOptions };
  }
}
